if (typeof ProductInfoTabs !== 'function') {

  class ProductInfoTabs extends HTMLElement {

    constructor() {
      super();

      this.tabs = this.querySelectorAll('.js-pit-tab');
      this.panels = this.querySelectorAll('.js-pit-panel');
      this.tabsNav = this.querySelector('.product-info-tabs__nav');
      this.currentIndex = 0;

      this.#init();
    }

    #init() {
      if (this.tabs.length === 0 || this.panels.length === 0) return;

      for (const tab of this.tabs) {
        tab.addEventListener('click', (e) => this.#handleTabClick(e));
        tab.addEventListener('keydown', (e) => this.#handleKeyDown(e));
      }

      // Set ARIA attributes
      this.#setAriaAttributes();
    }

    #setAriaAttributes() {
      this.tabsNav?.setAttribute('role', 'tablist');

      for (const [index, tab] of this.tabs.entries()) {
        const panelId = tab.getAttribute('data-panel');
        const isActive = tab.classList.contains('active');

        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('aria-controls', `panel-${panelId}`);
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        tab.id = `tab-${panelId}`;
      }

      for (const panel of this.panels) {
        const tabId = panel.getAttribute('data-tab');
        const isActive = panel.classList.contains('active');

        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', `tab-${tabId}`);
        panel.id = `panel-${tabId}`;

        if (!isActive) {
          panel.setAttribute('hidden', '');
        }
      }
    }

    #handleTabClick(event) {
      const clickedTab = event.currentTarget;

      if (clickedTab.classList.contains('active')) return;

      this.#activateTab(clickedTab);
    }

    #handleKeyDown(event) {
      const { key } = event;
      const tabsArray = Array.from(this.tabs);
      const currentIndex = tabsArray.indexOf(event.currentTarget);
      let newIndex;

      switch (key) {
        case 'ArrowLeft':
          newIndex = currentIndex - 1;
          if (newIndex < 0) newIndex = tabsArray.length - 1;
          break;
        case 'ArrowRight':
          newIndex = currentIndex + 1;
          if (newIndex >= tabsArray.length) newIndex = 0;
          break;
        case 'Home':
          newIndex = 0;
          break;
        case 'End':
          newIndex = tabsArray.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      const newTab = tabsArray[newIndex];
      newTab.focus();
      this.#activateTab(newTab);
    }

    #activateTab(tab) {
      const panelId = tab.getAttribute('data-panel');

      // Deactivate all tabs
      for (const t of this.tabs) {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
        t.setAttribute('tabindex', '-1');
      }

      // Deactivate all panels
      for (const p of this.panels) {
        p.classList.remove('active');
        p.setAttribute('hidden', '');
      }

      // Activate clicked tab
      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');
      tab.setAttribute('tabindex', '0');

      // Activate corresponding panel
      const panel = this.querySelector(`[data-tab="${panelId}"]`);

      if (panel) {
        panel.classList.add('active');
        panel.removeAttribute('hidden');
      }

      // Scroll tab into view on mobile
      if (window.innerWidth < 768 && this.tabsNav) {
        const tabRect = tab.getBoundingClientRect();
        const navRect = this.tabsNav.getBoundingClientRect();
        const scrollLeft = tab.offsetLeft - (navRect.width / 2) + (tabRect.width / 2);

        this.tabsNav.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }

      // Update current index
      this.currentIndex = Array.from(this.tabs).indexOf(tab);
    }

  }

  if (typeof customElements.get('product-info-tabs') === 'undefined') {
    customElements.define('product-info-tabs', ProductInfoTabs);
  }

}
